import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useIsViewer, VIEWER_NO_PERMISSION } from '@/hooks/useIsViewer';
import toastService from '@/services/toast/service';
import { changelogService, type ChangelogDetail as Detail } from '@/services/changelog';
import ImpactChart from './ImpactChart';

/** Mirrors the API's limit so the textarea stops where the server would refuse. */
const NOTES_MAX_LENGTH = 5000;

const formatLongDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** "21 July", for the overlap sentence; the year is already on the page. */
const formatDayMonth = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });

/** Absolute date in the viewer's timezone, for the "last edited" line. */
const formatEditedAt = (iso: string, timezone?: string | null) =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(timezone ? { timeZone: timezone } : {}),
  });

const ChangelogDetail = () => {
  const { slug = '' } = useParams();
  const { user } = useAuth();
  const isViewer = useIsViewer();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setNotFound(false);
    setEditingNotes(false);
    changelogService.detail(slug).then((data) => {
      if (cancelled) return;
      if (!data) setNotFound(true);
      else setDetail(data);
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const startEditingNotes = () => {
    setDraft(detail?.notes?.body ?? '');
    setEditingNotes(true);
  };

  const saveNotes = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const notes = await changelogService.saveNotes(detail.slug, draft);
      setDetail({ ...detail, notes });
      setEditingNotes(false);
      toastService.success('Notes saved');
    } catch {
      toastService.error("Couldn't save notes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-[1310px] mx-auto px-4 sm:px-0">
      <div className="w-full">
        {/* Once the entry is loaded the back arrow lives in the title row. */}
        {detail === null && (
          <Link
            to="/changelog"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Changelog
          </Link>
        )}

        {notFound ? (
          <Card className="mt-6 py-14 text-center text-sm text-muted-foreground">
            That changelog entry does not exist
          </Card>
        ) : detail === null ? (
          <div className="mt-5 space-y-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-[300px] w-full" />
          </div>
        ) : (
          <>
            {/* Compact bar: one row (back, title, area, date), then one muted
                paragraph carrying the description and any overlap. The charts
                are the point of the page, so they start right after. */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <div className="flex min-w-0 items-center gap-2.5">
                <Link
                  to="/changelog"
                  aria-label="Back to changelog"
                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Link>
                <h1 className="truncate text-lg sm:text-xl font-semibold tracking-tight">
                  {detail.title}
                </h1>
              </div>
              <span className="text-sm text-muted-foreground tabular-nums">
                {formatLongDate(detail.date)}
              </span>
            </div>

            {/* pre-line: a description can run to more than one paragraph. */}
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
              {detail.description}
              {detail.confounders.length > 0 && (
                <>
                  {' '}
                  <span className="text-amber-700 dark:text-amber-400">
                    Overlaps with{' '}
                    {detail.confounders.map((c, i) => (
                      <span key={c.slug}>
                        {i > 0 && (i === detail.confounders.length - 1 ? ' and ' : ', ')}
                        <Link to={`/changelog/${c.slug}`} className="underline underline-offset-2">
                          {c.title}
                        </Link>
                        , {formatDayMonth(c.date)}
                      </span>
                    ))}
                    . Movement below belongs to every change in the window.
                  </span>
                </>
              )}
            </p>
            {/* The charts cut days at UTC midnight for every viewer, not in
                the timezone on anyone's profile. Said once, here, so a reader
                comparing against a Stripe or GHL view knows why an edge case
                lands on the neighbouring day. */}
            <p className="mt-1.5 text-xs text-muted-foreground">
              Days on this page are counted in UTC, so the numbers read the same for everyone.
            </p>

            <div className="mt-4 border-t" />

            {detail.metrics.length === 0 ? (
              <Card className="mt-4 py-14 text-center text-sm text-muted-foreground">
                This entry does not declare a metric, so nothing is tracked for it
              </Card>
            ) : (
              <div className="space-y-3.5 mt-4">
                {detail.metrics.map((metric) => (
                  <ImpactChart
                    key={metric.key}
                    metric={metric}
                    slug={detail.slug}
                    changeDate={detail.date}
                    confounders={detail.confounders}
                  />
                ))}
              </div>
            )}
            {/* The team's own layer on top of the hardcoded entry. One field
                per entry; the backend stamps who saved last. */}
            <Card className="py-0 gap-0 mt-3.5">
              <CardHeader className="py-4 gap-0 border-b">
                <CardTitle className="text-base font-semibold">Notes</CardTitle>
                {!editingNotes && (
                  <CardAction>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0}>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={isViewer}
                            onClick={startEditingNotes}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
                    </Tooltip>
                  </CardAction>
                )}
              </CardHeader>
              <CardContent className="py-4">
                {editingNotes ? (
                  <>
                    <Textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      rows={4}
                      maxLength={NOTES_MAX_LENGTH}
                      placeholder="Add notes about this change"
                      autoFocus
                      disabled={saving}
                    />
                    <div className="mt-3 flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingNotes(false)}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button size="sm" onClick={saveNotes} disabled={saving}>
                        {saving ? 'Saving' : 'Save'}
                      </Button>
                    </div>
                  </>
                ) : detail.notes ? (
                  <>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{detail.notes.body}</p>
                    <p className="mt-3 text-xs text-muted-foreground">
                      Last edited {formatEditedAt(detail.notes.updatedAtUtc, user?.timezone)}
                      {detail.notes.updatedBy ? ` by ${detail.notes.updatedBy}` : ''}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No notes yet. Anything the team wants to remember about this change goes here.
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default ChangelogDetail;
